import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded'
import { toast } from 'react-toastify'
import { updateProgrammeObligation, getObligationScopeImpact } from 'services/ProgrammeService'
import { Tooltip } from '@mui/material'
import { UNIT_INK } from 'constants/entityColors'

const TONES = {
  building: '#0e7490',
  unit: UNIT_INK,
}

const labelFor = (scope, coverage, kind) => {
  const noun = kind === 'task' ? 'task' : 'file'
  if (scope === 'unit') {
    return coverage && coverage.applicable
      ? `Per-unit ${noun} · ${coverage.covered}/${coverage.applicable} units`
      : `Per-unit ${noun}`
  }
  return `Whole-building ${noun}`
}

const HintTarget = styled.span` display: inline-flex; `
const Hint = ({ title, children }) => (
  <Tooltip
    title={title}
    arrow
    placement='top'
    enterDelay={200}
    slotProps={{
      tooltip: { sx: { maxWidth: 280, fontSize: '11.5px', lineHeight: 1.5, padding: '8px 11px', bgcolor: '#1d2433', fontWeight: 500 } },
      arrow: { sx: { color: '#1d2433' } },
    }}
  >
    <HintTarget>{children}</HintTarget>
  </Tooltip>
)

const ObligationScopePill = ({ bpId, obligation, canEdit = false, kind = 'file', onChanged }) => {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)
  const [coords, setCoords] = useState(null)
  const [impact, setImpact] = useState(null)
  const [impactLoading, setImpactLoading] = useState(false)

  const scope = (obligation.scope || 'building') === 'unit' ? 'unit' : 'building'
  const nextScope = scope === 'unit' ? 'building' : 'unit'
  const coverage = obligation.unitCoverage || obligation.unit_coverage || null

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const margin = 12
      const popWidth = Math.min(320, window.innerWidth - 2 * margin)
      const left = Math.max(margin, Math.min(r.left, window.innerWidth - popWidth - margin))
      setCoords({ top: r.bottom + 8, left })
    }
  }, [open])

  useEffect(() => {
    if (!open || nextScope !== 'unit') return
    let cancelled = false
    setImpact(null)
    setImpactLoading(true)
    getObligationScopeImpact(bpId, obligation.id)
      .then(res => { if (!cancelled) setImpact(res?.data?.data || null) })
      .catch(() => { if (!cancelled) setImpact(null) })
      .finally(() => { if (!cancelled) setImpactLoading(false) })
    return () => { cancelled = true }
  }, [open, nextScope, bpId, obligation.id])

  const confirm = () => {
    if (saving) return
    setSaving(true)
    updateProgrammeObligation(bpId, obligation.id, { scope: nextScope })
      .then(() => {
        toast.success(nextScope === 'unit'
          ? (kind === 'task'
            ? 'Now required for every unit. Each needs its own completed task within the cadence.'
            : 'Now required for every unit. Evidence is tracked per Unit Passport.')
          : (kind === 'task'
            ? 'Now a whole-building requirement. One completed task satisfies it.'
            : 'Now a whole-building requirement. One current document satisfies it.'))
        setOpen(false)
        onChanged?.()
      })
      .catch(e => toast.error(e?.response?.data?.message || 'Could not change the requirement scope.'))
      .finally(() => setSaving(false))
  }

  if (!canEdit) {
    return (
      <Hint title='Whether one current document covers the whole building, or every unit needs its own.'>
        <Pill as='span' $tone={TONES[scope]}>
          {scope === 'unit' ? <MeetingRoomRoundedIcon style={{ fontSize: 12 }} /> : <ApartmentRoundedIcon style={{ fontSize: 12 }} />}
          {labelFor(scope, coverage, kind)}
        </Pill>
      </Hint>
    )
  }

  return (
    <>
      <Hint title='Change whether one current document covers the whole building, or every unit needs its own.'>
        <Pill
          ref={btnRef}
          type='button'
          $tone={TONES[scope]}
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
        >
          {scope === 'unit' ? <MeetingRoomRoundedIcon style={{ fontSize: 12 }} /> : <ApartmentRoundedIcon style={{ fontSize: 12 }} />}
          {labelFor(scope, coverage, kind)}
          <Caret>▾</Caret>
        </Pill>
      </Hint>

      {open && coords && createPortal(
        <Pop ref={popRef} role='dialog' aria-label='Change requirement scope' style={{ top: coords.top, left: coords.left }}>
          <PopTitle>
            {nextScope === 'unit'
              ? `Require this ${kind === 'task' ? 'task' : 'document'} for every unit?`
              : 'Make this a whole-building requirement?'}
          </PopTitle>
          <PopBody>
            {nextScope === 'unit'
              ? (kind === 'task'
                ? 'Each applicable unit will need its own completed task within the cadence. Tasks created for this obligation are automatically assigned to every applicable unit, and the score will reflect per-unit coverage.'
                : 'Each applicable unit will need its own current document, uploaded on its Unit Passport. A whole-building document will no longer satisfy this obligation, and the score will reflect per-unit coverage.')
              : (kind === 'task'
                ? 'One completed task within the cadence will satisfy this obligation for the whole building. Per-unit coverage will no longer be tracked for it.'
                : 'One current document for the whole building will satisfy this obligation. Per-unit coverage will no longer be tracked for it.')}
          </PopBody>
          {nextScope === 'unit' && (
            <ImpactBox>
              {impactLoading && <ImpactMuted>Checking how many units already have a document…</ImpactMuted>}
              {!impactLoading && impact && impact.applicable > 0 && (
                <>
                  <ImpactHead>What this changes now</ImpactHead>
                  <ImpactLine><b>{impact.applicable.toLocaleString()}</b> unit{impact.applicable === 1 ? '' : 's'} will be tracked for this obligation.</ImpactLine>
                  {impact.inReview > 0 && (
                    <ImpactLine><b>{impact.inReview.toLocaleString()}</b> already {impact.inReview === 1 ? 'has' : 'have'} a matching document: {impact.inReview === 1 ? 'it' : 'they'}’ll go to your review queue to approve.</ImpactLine>
                  )}
                  {(impact.pending + impact.expired) > 0 && (
                    <ImpactLine><b>{(impact.pending + impact.expired).toLocaleString()}</b> {(impact.pending + impact.expired) === 1 ? 'has' : 'have'} no current document yet. Shown as outstanding until one is added.</ImpactLine>
                  )}
                </>
              )}
              {!impactLoading && impact && impact.applicable === 0 && (
                <ImpactMuted>No units on this Building Passport yet. Nothing changes until units are added.</ImpactMuted>
              )}
            </ImpactBox>
          )}
          {nextScope === 'building' && coverage && coverage.applicable > 0 && (
            <ImpactBox>
              <ImpactLine>Per-unit tracking for <b>{coverage.applicable.toLocaleString()}</b> unit{coverage.applicable === 1 ? '' : 's'} will stop. A single whole-building document will satisfy this instead.</ImpactLine>
            </ImpactBox>
          )}
          <PopActions>
            <PopCancel type='button' onClick={() => setOpen(false)}>Cancel</PopCancel>
            <PopConfirm type='button' disabled={saving} $tone={TONES[nextScope]} onClick={confirm}>
              {saving ? 'Saving…' : (nextScope === 'unit' ? 'Require per unit' : 'Whole building')}
            </PopConfirm>
          </PopActions>
        </Pop>,
        document.body
      )}
    </>
  )
}

export default ObligationScopePill

const Pill = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone }) => `${$tone}33`};
  background: ${({ $tone }) => `${$tone}14`};
  color: ${({ $tone }) => $tone};
  font-size: 11px; font-weight: 800;
  cursor: ${({ as }) => (as === 'span' ? 'default' : 'pointer')};
  transition: background-color 160ms ease;
  &:hover { background: ${({ $tone, as }) => (as === 'span' ? `${$tone}14` : `${$tone}24`)}; }
`

const Caret = styled.span`
  font-size: 9px; opacity: 0.7;
`

const Pop = styled.div`
  position: fixed;
  z-index: 100100;
  width: 320px;
  max-width: calc(100vw - 24px);
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 18px 44px rgba(12, 18, 41, 0.22);
`

const PopTitle = styled.h5`
  margin: 0 0 6px;
  font-size: 13px; font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const PopBody = styled.p`
  margin: 0 0 12px;
  font-size: 12px; line-height: 1.5;
  color: #4d4d56;
`

const PopActions = styled.div`
  display: flex; justify-content: flex-end; gap: 8px;
`

const PopCancel = styled.button`
  font: inherit; background: #fff; border: 1px solid #ececec; border-radius: 8px;
  padding: 6px 12px; font-size: 12px; font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const PopConfirm = styled.button`
  font: inherit; border: none; border-radius: 8px;
  padding: 6px 14px; font-size: 12px; font-weight: 700;
  background: ${({ $tone }) => $tone};
  color: #fff; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(1.06); }
`

const ImpactBox = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  background: ${UNIT_INK}0f;
  border: 1px solid ${UNIT_INK}26;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ImpactHead = styled.span`
  font-size: 10.5px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.04em;
  color: ${UNIT_INK};
  margin-bottom: 2px;
`

const ImpactLine = styled.span`
  font-size: 12px; line-height: 1.45; color: #3a3a42;
  b { font-weight: 800; color: ${({ theme }) => theme.colors.haiti}; }
`

const ImpactMuted = styled.span`
  font-size: 12px; color: #6b6b73; font-style: italic;
`
